import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Button, Badge, Spinner, Alert, Modal } from 'react-bootstrap';

import coldemailer_api from '../coldemailer_api';
import coldemailer_config from '../coldemailer_config';
import '../coldemailer.css';
import '../coldemailer-styles.css';

// Matches the status enum on the EmailList model
const c_BadgeVariants = {
	inactive: 'secondary',
	active: 'success',
	archived: 'dark'
};

const format_date = (value) => {
	if(!value) {
		return '-';
	}

	const date = new Date(value);
	if(isNaN(date.getTime())) {
		return '-';
	}

	return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const get_badge_variant = (status) => {
	if(status && c_BadgeVariants[status]) {
		return c_BadgeVariants[status];
	}

	return 'secondary';
};

function EmailLists() {
	const navigate = useNavigate();

	const [email_lists, set_email_lists] = useState([]);
	const [loading, set_loading] = useState(true);
	const [error, set_error] = useState('');

	// The list awaiting confirmation, or null when the modal is closed
	const [confirm_delete, set_confirm_delete] = useState(null);
	const [deleting, set_deleting] = useState(false);

	const load_email_lists = async () => {
		set_loading(true);
		set_error('');

		try {
			const lists = await coldemailer_api.get_email_lists();
			set_email_lists(lists);
		} catch (error) {
			console.log('EmailLists.load_email_lists - ERROR: ', error);
			set_error('Failed to load email lists.');
		} finally {
			set_loading(false);
		}
	};

	useEffect(function() {
		load_email_lists();
	}, []);

	const on_row_click = (email_list) => {
		navigate(coldemailer_config.email_list_route + '/' + email_list.id);
	};

	// The button sits inside a row that navigates, so the row's own click must not also fire
	const on_delete_click = (event, email_list) => {
		event.stopPropagation();
		set_confirm_delete(email_list);
	};

	const on_cancel_delete = () => {
		if(deleting) {
			return;
		}

		set_confirm_delete(null);
	};

	const on_confirm_delete = async () => {
		set_deleting(true);
		set_error('');

		try {
			await coldemailer_api.delete_email_list(confirm_delete.id);
			set_confirm_delete(null);
			await load_email_lists();
		} catch (error) {
			console.log('EmailLists.on_confirm_delete - ERROR: ', error);
			set_error('Failed to delete the email list.');
			set_confirm_delete(null);
		} finally {
			set_deleting(false);
		}
	};

	const render_panel_header = () => {
		return (
			<div className="ce-panel-header">
				<h5 className="ce-panel-title">Email Lists</h5>

				<div className="ce-header-actions">
					<Button variant="outline-light" size="sm" className="ce-button" onClick={load_email_lists}>
						Refresh
					</Button>
				</div>
			</div>
		);
	};

	const render_empty_row = () => {
		return (
			<tr>
				<td colSpan={6} className="ce-empty">
					No email lists yet.
				</td>
			</tr>
		);
	};

	const render_row = (email_list) => {
		return (
			<tr key={email_list.id} className="ce-row-clickable" onClick={function() { on_row_click(email_list); }}>
				<td className="ce-el-col-name">
					{email_list.name ? email_list.name : '-'}
				</td>
				<td className="ce-el-col-description">
					{email_list.description ? email_list.description : '-'}
				</td>
				<td className="ce-el-col-members">
					{email_list.member_count === null || email_list.member_count === undefined ? 0 : email_list.member_count}
				</td>
				<td className="ce-el-col-status">
					<Badge bg={get_badge_variant(email_list.status)}>
						{email_list.status ? email_list.status : 'unknown'}
					</Badge>
				</td>
				<td className="ce-el-col-created">
					{format_date(email_list.createdAt)}
				</td>
				<td className="ce-el-col-actions">
					<Button
						variant="danger"
						size="sm"
						className="ce-delete-button"
						onClick={function(event) { on_delete_click(event, email_list); }}
					>
						Delete
					</Button>
				</td>
			</tr>
		);
	};

	const render_table = () => {
		return (
			<Table striped bordered hover responsive variant="dark" className="ce-table">
				<thead>
					<tr>
						<th className="ce-el-col-name">Name</th>
						<th className="ce-el-col-description">Description</th>
						<th className="ce-el-col-members">Members</th>
						<th className="ce-el-col-status">Status</th>
						<th className="ce-el-col-created">Created</th>
						<th className="ce-el-col-actions"></th>
					</tr>
				</thead>
				<tbody>
					{email_lists.length === 0 ? render_empty_row() : email_lists.map(render_row)}
				</tbody>
			</Table>
		);
	};

	const render_body = () => {
		if(loading) {
			return (
				<div className="ce-loading">
					<Spinner animation="border" size="sm" />
				</div>
			);
		}

		return render_table();
	};

	// Names the list and how many companies go with it, since the delete cannot be undone
	const render_confirm_modal = () => {
		const member_count = (confirm_delete && confirm_delete.member_count) ? confirm_delete.member_count : 0;

		return (
			<Modal show={confirm_delete !== null} onHide={on_cancel_delete} centered>
				<Modal.Header closeButton>
					<Modal.Title>Delete email list</Modal.Title>
				</Modal.Header>

				<Modal.Body className="ce-modal-body">
					<p className="ce-modal-counts">
						{'Delete "' + (confirm_delete ? confirm_delete.name : '') + '" and its ' + member_count + ' list members? Companies in Lead Discovery are not affected. This cannot be undone.'}
					</p>
				</Modal.Body>

				<Modal.Footer>
					<Button variant="outline-secondary" onClick={on_cancel_delete} disabled={deleting}>
						Cancel
					</Button>

					<Button variant="danger" onClick={on_confirm_delete} disabled={deleting}>
						{deleting ? 'Deleting...' : 'Delete'}
					</Button>
				</Modal.Footer>
			</Modal>
		);
	};

	return (
		<div className="ce-panel ce-lists-panel">
			{render_panel_header()}

			{error ? <Alert variant="danger">{error}</Alert> : null}

			{render_body()}

			{render_confirm_modal()}
		</div>
	);
}

export default EmailLists;
